import {
  ellipsis, H3, Link, nowrap, ProductPriceBlock, ProductPriceBlockSavePrice, Rating
} from '@codex/basics';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';
import ProductImage from '../../ProductImage/index';

const Container = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  margin-bottom: 0;
  overflow: hidden;
  
  @media ${({ theme }) => theme.breakpoints.tight} {
    padding: ${({ theme }) => theme.spacing[2]};
  }
  
  padding-top: ${({ theme }) => theme.spacing[3]};
  
  > ${H3} {
    ${ellipsis()};
    ${nowrap()};
    line-height: 1.1;
    font-weight: ${({ theme }) => theme.fontWeights.normal};
    margin-bottom: ${({ theme }) => theme.spacing[3]};
    
    @media ${({ theme }) => theme.breakpoints.tight} {
      margin-bottom: ${({ theme }) => theme.spacing[2]};
    }
  }
  
  ${props => props.appearance === 'box' && css`
    border: 1px solid ${props.theme.colors.shades[5]};
    background-color: ${props.theme.colors.shades[7]};
  `};
  
  ${ProductPriceBlockSavePrice} {
    font-size: 12px;
  }
`;

const Content = styled.div`
  display: flex;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme, priceDropped }) => !priceDropped && theme.spacing[1]};
  margin-left: 25px;
  padding-right: ${({ theme }) => theme.spacing[1]};
  align-items: stretch;
  width: 100%;
  
  @media ${({ theme }) => theme.breakpoints.tight} {
    margin-left: ${({ theme }) => theme.spacing[4]};
  }
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  
  ${ProductImage} {
    margin-left: ${({ theme }) => theme.spacing[1]};
    
    @media ${({ theme }) => theme.breakpoints.tight} {
      margin-left: 0;
    }
  }
`;

const RatingLink = styled(Link)`
  margin-top: ${({ theme }) => theme.spacing[2]};
  text-align: center;
  font-size: 12px;
`;

const NoRatingsMessage = styled.span`
  margin-top: ${({ theme }) => theme.spacing[2]};
  text-align: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.shades[1]};
`;

const detectPriceDrop = (rawPrice, rawPriceWhenSaved) => (
  !!rawPrice && !!rawPriceWhenSaved && parseFloat(rawPriceWhenSaved) > parseFloat(rawPrice)
);

const Item = props => (
  <Container appearance={props.appearance} className={props.className}>
    <H3>
      <Link
        href={props.productUrl}
        data-interaction="Saved items module: item: product name"
      >
        {props.name}
      </Link>
    </H3>
    <Content>
      <ImageContainer>
        <Link
          href={props.productUrl}
          data-interaction="Saved items module: item: product image"
        >
          <ProductImage
            src={props.image}
            alt={props.name}
          />
        </Link>
      </ImageContainer>
      <Column priceDropped={detectPriceDrop(props.rawPrice, props.rawPriceWhenSaved)}>
        <ProductPriceBlock
          currentPrice={props.price}
          savingsPrice={props.savePrice}
          priceDropped={detectPriceDrop(props.rawPrice, props.rawPriceWhenSaved)}
        />
        {props.ratingCount > 0
          ? (
            <RatingLink
              href={`${props.productUrl}#tab3`}
              data-interaction="Saved items module: item: product rating"
            >
              <Rating
                maxCount={5}
                ratingValue={props.rating}
                ratingCount={props.ratingCount}
              />
            </RatingLink>
          )
          : (
            <NoRatingsMessage>
              {props.noRatingsLabel} (0)
            </NoRatingsMessage>
          )}
      </Column>
    </Content>
  </Container>
);

Item.displayName = 'RecentlySavedItem';

Item.propTypes = {
  appearance: PropTypes.string,
  className: PropTypes.string,
  price: PropTypes.string.isRequired,
  productUrl: PropTypes.string.isRequired,
  rating: PropTypes.number,
  ratingCount: PropTypes.number,
  savePrice: PropTypes.string,
  name: PropTypes.string.isRequired,
  noRatingsLabel: PropTypes.string,
  image: PropTypes.string.isRequired,
  rawPrice: PropTypes.number.isRequired,
  rawPriceWhenSaved: PropTypes.number.isRequired
};

Item.defaultProps = {
  appearance: 'box',
  className: undefined,
  noRatingsLabel: 'No reviews yet',
  rating: 0,
  ratingCount: 0,
  savePrice: ''
};

export default Item;
